import axios from "axios";

import { getBaseUrl } from "./base-url";
import type { SheetsPickerConfig } from "./sheets-oauth";

/**
 * MOSA-364. The two calls the wizard makes before a datasource exists.
 *
 * Both postdate `src/lib/generated/api-client.ts`, and regenerating that needs
 * a live API, so this module uses axios directly, the same way `sheets-oauth.ts`
 * does. When the client is next regenerated these can move into it.
 */

export type SheetsWizardColumn = {
  name: string;
  index: number;
  formula: boolean;
  /** MOSA-408. A protected range covers this column: the panel cannot write. */
  protected: boolean;
};

export type SheetsWizardTab = {
  sheetId: number;
  gid: number;
  title: string;
  /** False when the tab's header row is missing or blank. */
  hasHeader: boolean;
  /** MOSA-408. The 1-based row the column names came from. */
  headerRow: number;
  /** MOSA-408. A merged cell covers the header row, so names are unreliable. */
  mergedHeader: boolean;
  /** MOSA-408. A protected range covers the whole tab. */
  protectedTab: boolean;
  columns: SheetsWizardColumn[];
  rowCount: number;
  /** MOSA-408. False for a headerless tab and past the preselect limit. */
  selected: boolean;
};

export type SheetsWizard = {
  spreadsheetId: string;
  title: string;
  tabs: SheetsWizardTab[];
  totalCells: number;
  maxCells: number;
  gid: number | null;
  /**
   * MOSA-408. False when this Google account can open the file but not change
   * it. The review screen then offers a read-only connect.
   */
  canEdit: boolean;
  tabCount: number;
  /** MOSA-408. True when the file has more tabs than the probe preselects. */
  tabsTruncated: boolean;
};

/**
 * MOSA-408. Fill in the fields an older API build does not send, so the review
 * screen reads one shape and never `undefined`. A missing `canEdit` means
 * "writable", because that is what every build before this one implied.
 */
/**
 * The probe body as it may actually arrive: any field can be absent, at any
 * depth, because an older API build did not send it.
 */
export type PartialSheetsWizard = Partial<Omit<SheetsWizard, "tabs">> & {
  tabs?: Array<
    Partial<Omit<SheetsWizardTab, "columns">> & {
      columns?: Array<Partial<SheetsWizardColumn>>;
    }
  >;
};

export function normalizeWizard(
  raw: PartialSheetsWizard | null | undefined,
): SheetsWizard {
  const tabs: SheetsWizardTab[] = (raw?.tabs ?? []).map((tab) => ({
    sheetId: tab?.sheetId ?? 0,
    gid: tab?.gid ?? tab?.sheetId ?? 0,
    title: tab?.title ?? "",
    hasHeader: tab?.hasHeader === true,
    headerRow:
      Number.isInteger(tab?.headerRow) && (tab?.headerRow ?? 0) >= 1
        ? (tab.headerRow as number)
        : 1,
    mergedHeader: tab?.mergedHeader === true,
    protectedTab: tab?.protectedTab === true,
    columns: (tab?.columns ?? []).map((column, index) => ({
      name: column?.name ?? "",
      index: Number.isInteger(column?.index) ? (column.index as number) : index,
      formula: column?.formula === true,
      protected: column?.protected === true,
    })),
    rowCount: Number(tab?.rowCount ?? 0),
    // An older build has no `selected`; every tab with a header was used then.
    selected:
      tab?.selected === undefined
        ? tab?.hasHeader === true
        : tab.selected === true,
  }));
  return {
    spreadsheetId: raw?.spreadsheetId ?? "",
    title: raw?.title ?? "",
    tabs,
    totalCells: Number(raw?.totalCells ?? 0),
    maxCells: Number(raw?.maxCells ?? 0),
    gid: raw?.gid ?? null,
    canEdit: raw?.canEdit !== false,
    tabCount: Number.isInteger(raw?.tabCount)
      ? (raw?.tabCount as number)
      : tabs.length,
    tabsTruncated: raw?.tabsTruncated === true,
  };
}

const authHeaders = (token: string | null) =>
  token ? { Authorization: `Bearer ${token}` } : {};

export type SheetsLinkCheck = {
  ok: boolean;
  spreadsheetId?: string;
  gid?: number | null;
  canonicalUrl?: string;
  /** Present when the API could resolve the spreadsheet title. */
  title?: string;
  code?: string;
  message?: string;
};

/**
 * Step 1. Ask the API to parse the link with the same rules the app used, so a
 * link the app accepted cannot fail differently on the server.
 */
export async function checkSheetsLink(
  token: string | null,
  link: string,
): Promise<SheetsLinkCheck> {
  const response = await axios.post<SheetsLinkCheck>(
    `${getBaseUrl()}/datasources/sheets/link-check`,
    { link },
    { headers: authHeaders(token) },
  );
  return { ...response.data, ok: Boolean(response.data?.ok) };
}

/**
 * Steps 4 and 5. Read the sheet through the caller's own Google connection.
 * Rejects with the API's typed error, which `sheetsFailureFromError` maps.
 *
 * MOSA-908. A Drive browse pick may send `pickedFileId` with an empty link.
 * The server builds the canonical URL. Prefer a link when both are present.
 */
export async function probeSheets(
  token: string | null,
  args: {
    link?: string;
    pickedFileId?: string;
    connectionId: string;
    gid?: number | null;
    /** MOSA-408. Re-read the names from this 1-based row, not from row 1. */
    headerRow?: number | null;
  },
): Promise<SheetsWizard> {
  const params = new URLSearchParams({
    connectionId: args.connectionId,
  });
  if (args.link) {
    params.set("link", args.link);
  }
  if (args.pickedFileId) {
    params.set("pickedFileId", args.pickedFileId);
  }
  if (args.gid != null) {
    params.set("gid", String(args.gid));
  }
  if (args.headerRow != null && args.headerRow > 1) {
    params.set("headerRow", String(args.headerRow));
  }
  const response = await axios.get<SheetsWizard>(
    `${getBaseUrl()}/datasources/sheets/probe?${params.toString()}`,
    { headers: authHeaders(token) },
  );
  return normalizeWizard(response.data);
}

/**
 * MOSA-408. Copy an Excel or CSV upload in Drive into a real Google Sheet.
 * The upload is left as it is; the copy becomes the panel's source, which is
 * what the wizard says before it calls this.
 */
export type SheetsConvertResult = {
  spreadsheetId: string;
  title: string;
  /** True when the file was already a Google Sheet, so nothing was copied. */
  alreadyASheet: boolean;
  canonicalUrl: string;
};

export async function convertSheetsFile(
  token: string | null,
  args: { fileId: string; connectionId: string },
): Promise<SheetsConvertResult> {
  const response = await axios.post<SheetsConvertResult>(
    `${getBaseUrl()}/datasources/sheets/convert`,
    { fileId: args.fileId, connectionId: args.connectionId },
    { headers: authHeaders(token) },
  );
  return response.data;
}

/**
 * Step 3. Ask for the consent URL and the Picker config in one call. This is
 * the MOSA-146 route; the wizard uses it exactly as the connect form does.
 */
export type PickerConfig = SheetsPickerConfig;

/**
 * Step 4. The Google Picker is a browser widget and needs a Google *access*
 * token, not our connection id. The API returns a short-lived one for the
 * caller's own live connection, scope drive.file. The token stays in memory
 * and is never stored or logged.
 */
export async function pickerAccessToken(
  token: string | null,
  connectionId: string,
): Promise<{ accessToken: string; scope: string }> {
  const response = await axios.post<{
    accessToken?: string;
    scope?: string;
  }>(
    `${getBaseUrl()}/auth/google/sheets/picker-token`,
    { connectionId },
    { headers: authHeaders(token) },
  );
  const accessToken = response.data?.accessToken;
  if (!accessToken) {
    throw new Error("The Picker token was not returned.");
  }
  return {
    accessToken,
    scope: response.data?.scope ?? "",
  };
}

/**
 * MOSA-468. The Google account behind the caller's own connection, for the
 * "Connected as <email> · Switch account" line. The API checks ownership: a
 * foreign or revoked connection is a reconnect, never another user's email.
 */
export async function connectionAccount(
  token: string | null,
  connectionId: string,
): Promise<{ email: string; scope: string }> {
  const response = await axios.post<{ email?: string; scope?: string }>(
    `${getBaseUrl()}/auth/google/sheets/account`,
    { connectionId },
    { headers: authHeaders(token) },
  );
  const email = response.data?.email;
  if (!email) {
    throw new Error("The Google account was not returned.");
  }
  return { email, scope: response.data?.scope ?? "" };
}
